import { useState, useEffect, useRef } from 'react'
import { ChatMessage } from '@/types/chat.types'
import { cn } from '@/lib/utils'
import { format } from 'date-fns'
import { Check, CheckCheck, Download, Expand, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
    Dialog,
    DialogContent,
    DialogClose,
    DialogTitle,
} from '@/components/ui/dialog'
import { VisuallyHidden } from '@/components/ui/visually-hidden'
import { useTranslation } from '@/hooks/useTranslation'

interface MessageProps {
    message: ChatMessage
    agentId?: string
    onObserve?: (element: HTMLElement | null) => void
    showSenderInfo?: boolean
    visitorName?: string
}

export function Message({ message, agentId, onObserve, showSenderInfo = false, visitorName }: MessageProps) {
    const { t } = useTranslation()
    const [isImageModalOpen, setIsImageModalOpen] = useState(false)
    const messageRef = useRef<HTMLDivElement>(null)
    const isAgent = message.sender_type === 'agent'
    const isOwnMessage = isAgent && message.agent_id === agentId
    const isRead = !!message.read_by_agent_at

    // Notify parent about this element for observation
    useEffect(() => {
        if (onObserve && messageRef.current) {
            onObserve(messageRef.current)
        }

        return () => {
            if (onObserve && messageRef.current) {
                // Cleanup observation when component unmounts
                onObserve(null)
            }
        }
    }, [onObserve])

    const handleDownload = async (url: string) => {
        try {
            const response = await fetch(url)
            const blob = await response.blob()
            const downloadUrl = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = downloadUrl
            link.download = `chat-image-${Date.now()}.png`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(downloadUrl)
        } catch (error) {
            console.error('Failed to download image:', error)
        }
    }


    return (
        <>
            <div
                ref={messageRef}
                data-message-id={message.id}
                data-sender-type={message.sender_type}
                data-is-read={isRead}
                className={cn(
                    'flex flex-col w-full',
                    isAgent ? 'items-end' : 'items-start'
                )}
                role="article"
                aria-label={t('message.messageFrom', { 
                    sender: isAgent ? t('message.agent') : t('message.visitor'),
                    time: format(new Date(message.created_at), 'p')
                })}
            >
                {/* Sender attribution - only show for visitor messages on first message in sequence */}
                {!isAgent && showSenderInfo && (
                    <div className="flex items-center gap-2 px-1 mb-1">
                        <span className="text-xs font-medium text-foreground/80">
                            {visitorName || t('message.visitor')}
                        </span>
                        {!isRead && (
                            <Badge className="h-4 px-1.5 bg-blue-500 text-white text-[10px] font-bold border-0">
                                {t('message.new')}
                            </Badge>
                        )}
                    </div>
                )}

                {/* Message bubble */}
                <div
                    className={cn(
                        'px-4 py-3 rounded-2xl max-w-[85%] sm:max-w-[70%]',
                        isAgent ? [
                            'bg-gradient-to-br from-primary to-primary/90',
                            'text-primary-foreground shadow-md rounded-br-md'
                        ] : [
                            'bg-muted shadow-sm border border-border/50 rounded-tl-md',
                            !isRead && 'ring-2 ring-blue-500/30 bg-blue-50/80 dark:bg-blue-950/30'
                        ]
                    )}>
                    {message.content && (
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">
                            {message.content}
                        </p>
                    )}

                    {message.media_url && (
                        <div className="relative group mt-2">
                            <img
                                src={message.media_url}
                                alt={t('message.uploadedMedia')}
                                className="w-full max-w-[200px] sm:max-w-[300px] md:max-w-[400px] h-auto max-h-[150px] sm:max-h-[200px] md:max-h-[250px] object-cover rounded-lg cursor-pointer transition-opacity hover:opacity-90"
                                onClick={() => setIsImageModalOpen(true)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault()
                                        setIsImageModalOpen(true)
                                    }
                                }}
                                aria-label={t('message.clickToViewFullSizeImage')}
                            />
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex gap-1 z-10">
                                <Button
                                    size="icon"
                                    variant="secondary"
                                    className="h-10 w-10 bg-background/80 backdrop-blur-sm hover:bg-background/90"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        setIsImageModalOpen(true)
                                    }}
                                    aria-label={t('message.viewFullSizeImage')}>
                                    <Expand className="h-4 w-4" />
                                </Button>
                                <Button
                                    size="icon"
                                    variant="secondary"
                                    className="h-10 w-10 bg-background/80 backdrop-blur-sm hover:bg-background/90"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleDownload(message.media_url!)
                                    }}
                                    aria-label={t('message.downloadImage')}>
                                    <Download className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Timestamp + Read receipt */}
                    <div
                        className={cn(
                            'text-xs mt-2 flex items-center gap-1.5',
                            isAgent ? 'text-primary-foreground/80 justify-end' : 'text-muted-foreground'
                        )}>
                        <time>{format(new Date(message.created_at), 'p')}</time>
                        {isOwnMessage && (
                            <span
                                className="inline-flex items-center"
                                aria-label={isRead ? t('message.messageRead') : t('message.messageSent')}
                            >
                                {isRead ? (
                                    <CheckCheck className="h-3.5 w-3.5 text-blue-200" />
                                ) : (
                                    <Check className="h-3.5 w-3.5" />
                                )}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Full-size image modal */}
            {message.media_url && (
                <Dialog open={isImageModalOpen} onOpenChange={setIsImageModalOpen}>
                    <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 overflow-hidden">
                        <VisuallyHidden>
                            <DialogTitle>{t('message.viewImage')}</DialogTitle>
                        </VisuallyHidden>
                        <div className="relative w-full h-full max-h-[90vh] overflow-auto bg-background/95">
                            <img
                                src={message.media_url}
                                alt={t('message.fullSizeMedia')}
                                className="w-full h-auto object-contain"
                            />
                            <div className="absolute top-4 right-4 flex gap-2 z-10">
                                <Button
                                    size="icon"
                                    variant="secondary"
                                    className="h-10 w-10 bg-background/90 backdrop-blur-sm hover:bg-background shadow-md"
                                    onClick={() => handleDownload(message.media_url!)}
                                    aria-label={t('message.downloadImage')}>
                                    <Download className="h-4 w-4" />
                                </Button>
                                <DialogClose asChild>
                                    <Button
                                        size="icon"
                                        variant="secondary"
                                        className="h-10 w-10 bg-background/90 backdrop-blur-sm hover:bg-background shadow-md"
                                        aria-label={t('message.closeImageView')}>
                                        <X className="h-4 w-4" />
                                    </Button>
                                </DialogClose>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </>
    )
}
